import { Dialog } from '@kobalte/core/dialog'
import { useStore } from '@nanostores/solid'
import { Fit, Layout, Rive, RiveFile } from '@rive-app/canvas-lite'
import { createSignal, onCleanup, onMount } from 'solid-js'
import bgImg from '~/assets/bg.svg'
import subscribedRiv from '~/assets/rive/subscribed.riv'
import { platform } from '~/lib/params'
import { m } from '~/paraglide/messages'
import { $isInTrial } from '~/store/service-level'

export function SubscribedView(props: { onDone: () => void }) {
  const isInTrial = useStore($isInTrial)

  const titleMessage = () => {
    if (platform === 'china_android') {
      return isInTrial()
        ? m.onboarding_paywall_subscribed_success_title_trial_cna()
        : m.onboarding_paywall_subscribed_success_title_cna()
    }
    return m.onboarding_paywall_subscribed_success_title()
  }

  const contentMessage = () => {
    if (platform === 'china_android') {
      return m.onboarding_paywall_subscribed_success_content_trial_cna()
    }
    return m.onboarding_paywall_subscribed_success_content()
  }

  const riveFile = new RiveFile({ src: subscribedRiv })
  const loadingRiveFile = (async () => {
    await riveFile.init()
    return riveFile
  })()

  const [isBtnRevealed, setIsBtnRevealed] = createSignal(false)
  const revealTimer = window.setTimeout(() => setIsBtnRevealed(true), 4800)
  onCleanup(() => window.clearTimeout(revealTimer))

  return (
    <Dialog open>
      <Dialog.Content
        class="fixed inset-0 z-[90]"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div
          class="size-full bg-[#001721] px-8 pt-[calc(60px+var(--safe-area-top))] text-center text-[#F3FEFF] animate-in fade-in [animation-duration:300ms]"
          style={{
            'background-image': `url(${bgImg})`,
            'background-repeat': 'no-repeat',
            'background-size': 'cover',
            'background-position': 'center',
          }}
        >
          <div class="h-[30vh] w-full">
            <Anim loadingRiveFile={loadingRiveFile} />
          </div>

          <h1
            class="mt-6 text-[32px] font-bold leading-tight animate-in fade-in slide-in-from-bottom-12 fill-mode-both [animation-delay:3s] [animation-duration:600ms]"
            style={{
              'text-shadow': '0 0 10px rgba(255, 255, 255, 0.5)',
            }}
          >
            {titleMessage()}
          </h1>

          <p class="mt-2 flex h-[112px] items-center justify-center text-[20px] font-bold animate-in fade-in slide-in-from-bottom-12 fill-mode-both [animation-delay:3.5s] [animation-duration:600ms]">
            {contentMessage()}
          </p>
        </div>

        <div class="absolute bottom-12 left-0 right-0 px-4 animate-in fade-in fill-mode-both [animation-delay:4.5s] [animation-duration:300ms]">
          <button
            type="button"
            class="group h-[52px] w-full rounded-xl p-px [background-image:linear-gradient(180deg,#C5FF70_0%,#69FF78_45%,#30D3FF_100%)]"
            disabled={!isBtnRevealed()}
            onClick={props.onDone}
          >
            <span class="flex h-full w-full items-center justify-center rounded-[11px] font-bold text-[#176361] [background-image:linear-gradient(106deg,#C5FF70_13%,#30D3FF_97%)] group-active:[background-image:linear-gradient(90deg,#94D66E_0%,#2BB8D3_100%)]">
              {m.onboarding_paywall_subscribed_success_btn()}
            </span>
          </button>
        </div>
      </Dialog.Content>
    </Dialog>
  )
}

function Anim(props: { loadingRiveFile: Promise<RiveFile> }) {
  let canvas!: HTMLCanvasElement // oxlint-disable-line eslint/no-unassigned-vars
  let rive: Rive | null = null
  const handleResize = () => {
    rive?.resizeDrawingSurfaceToCanvas()
  }
  onMount(() => {
    props.loadingRiveFile.then((riveFile) => {
      rive = new Rive({
        riveFile,
        artboard: 'paywall_success',
        stateMachines: ['State Machine 1'],
        canvas,
        autoplay: true,
        layout: new Layout({ fit: Fit.FitHeight }),
        onLoad: () => {
          rive?.resizeDrawingSurfaceToCanvas()
          window.addEventListener('resize', handleResize)
        },
      })
    })
  })
  onCleanup(() => {
    rive?.stopRendering()
    rive?.cleanupInstances()
    rive = null
    window.removeEventListener('resize', handleResize)
  })
  return <canvas ref={canvas} class="size-full" />
}
